import { IconButton, SvgCheck, SvgEdit } from '@loomhq/lens';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { useDispatch, useSelector } from 'react-redux';

import { updateTextBlocks } from 'actions/textOverlay';
import {
  ROLLOUT_TEXT_TO_SPEECH,
  ROLLOUT_TEXT_TO_SPEECH_VIEWING,
} from 'constants/featureFlag';
import {
  selectVideoHeight,
  selectVideoId,
  selectVideoWidth,
} from 'selectors/video';
import {
  selectCurrentUserIsOwner,
  selectPlayerTime,
} from 'selectors/videoPlayer';

import useClickOutside from 'hooks/useClickOutside';
import { useFeatureFlagValue } from 'hooks/featureFlag';

import TextToSpeechControls from '../text-to-speech-controls';
import {
  getTextBlocksRequest,
  sendTextBlocksUpdate,
} from '../../../utilities/text-overlay';

import styles from './styles.module.less';

const clearSelection = () => {
  const sel = window.getSelection();

  sel.removeAllRanges();
};

const selectElementContents = el => {
  const range = document.createRange();

  range.selectNodeContents(el);

  const sel = window.getSelection();

  sel.removeAllRanges();
  sel.addRange(range);
};

const TextBlock = ({
  block,
  onInputChange,
  onBlockUpdate,
  saveBlock,
  backgroundColor = 'var(--lns-color-success)',
  color = 'white',
  textId,
}) => {
  const { selected, text, width, startTs, endTs } = block;

  const [editing, setEditing] = useState(null);
  const textRef = useRef();
  const playTime = useSelector(selectPlayerTime);
  const isVideoOwner = useSelector(selectCurrentUserIsOwner);
  const editingEnabled = useFeatureFlagValue(ROLLOUT_TEXT_TO_SPEECH);

  const onClickOutside = useCallback(() => {
    if (selected) {
      onBlockUpdate({ ...block, selected: false }, textId);
    }

    if (editing) {
      setEditing(false);
    }
  }, [selected, textId, block, onBlockUpdate, editing]);

  const ref = useClickOutside(onClickOutside, 't2s-menu-items');

  const halfWidth = width / 2;

  const wrapperStyles = {
    position: 'relative',
    left: `calc(50% - ${halfWidth}px)`,
    top: `20%`,
  };

  const inlineStyles = {
    position: 'relative',
    width: `${width}px`,
    backgroundColor,
    color,
    borderRadius: '6px',
    fontWeight: 600,
    fontSize: '2vh',
    textAlign: 'center',
    padding: 'var(--lns-space-small) var(--lns-space-medium)',
  };

  const onInput = ev => {
    const newText = ev.target.innerText;

    onInputChange(newText, textId);
  };

  const isVisible = playTime >= startTs && playTime <= endTs;

  useEffect(() => {
    if (editing) {
      selectElementContents(textRef.current);
    } else if (editing === false) {
      clearSelection();
      saveBlock();
    }
  }, [editing]);

  const onMouseClick = useCallback(() => {
    if (!selected) {
      onBlockUpdate({ ...block, selected: true }, textId);
    }
  }, [selected, block]);

  return (
    <Draggable disabled={editing}>
      <div
        id="text-to-speech-container"
        style={wrapperStyles}
        onClick={onMouseClick}
        ref={ref}
        className={classNames(
          styles.textWrapper,
          editing && styles.editing,
          isVisible && styles.isVisible
        )}
      >
        <div
          className={classNames(
            styles.textItem,
            selected && styles.selectedItem
          )}
          onInput={onInput}
          contentEditable={editing}
          style={inlineStyles}
          ref={textRef}
        >
          {text}
          {isVideoOwner && editingEnabled && (
            <div
              className={styles.editButton}
              onClick={() => setEditing(!editing)}
            >
              <IconButton
                altText="Edit Text"
                icon={editing ? <SvgCheck /> : <SvgEdit />}
              />
            </div>
          )}
        </div>
      </div>
    </Draggable>
  );
};

const TextToSpeechOverlayWrapper = () => {
  const text2SpeechVisible = useSelector(state => state.textOverlay.show);
  const hasViewingEnabled = useFeatureFlagValue(ROLLOUT_TEXT_TO_SPEECH_VIEWING);

  if (!text2SpeechVisible) {
    return null;
  }

  if (!hasViewingEnabled) {
    return null;
  }

  return <TextToSpeechOverlay />;
};

const TextToSpeechOverlay = () => {
  const dispatch = useDispatch();

  const textBlocks = useSelector(state => state.textOverlay.textBlocks);
  const videoWidth = useSelector(selectVideoWidth);
  const videoHeight = useSelector(selectVideoHeight);
  const videoId = useSelector(selectVideoId);

  const containerRef = useRef();
  const boundsRef = useRef();

  const boundingStyles = {
    aspectRatio: `${videoWidth}/${videoHeight}`,
  };

  const updateBlockText = (newText, idx) => {
    textBlocks[idx].text = newText;

    dispatch(updateTextBlocks(textBlocks));
  };

  const onBlockUpdate = useCallback(
    (updatedBlock, idx) => {
      textBlocks[idx] = updatedBlock;

      dispatch(updateTextBlocks([...textBlocks]));
    },
    [dispatch, textBlocks]
  );

  const saveTextBlocks = useCallback(() => {
    sendTextBlocksUpdate(videoId, textBlocks);
  }, [textBlocks, videoId]);

  useEffect(() => {
    getTextBlocksRequest(videoId).then(fetchedItems => {
      dispatch(updateTextBlocks(fetchedItems));
    });
  }, [videoId, dispatch]);

  if (!textBlocks.length) {
    return <TextToSpeechControls />;
  }

  return (
    <div ref={containerRef} className={styles.container}>
      <TextToSpeechControls />

      <div
        ref={boundsRef}
        style={boundingStyles}
        className={styles.videoBounds}
      >
        {textBlocks.map((block, idx) => {
          return (
            <TextBlock
              onBlockUpdate={onBlockUpdate}
              onInputChange={updateBlockText}
              key={idx}
              textId={idx}
              block={block}
              saveBlock={saveTextBlocks}
            />
          );
        })}
      </div>
    </div>
  );
};

export default TextToSpeechOverlayWrapper;
